// src/oura/newscan/domain.ts
//
// The one place the New scan flow keys a website. Two strengths:
//
// - normalizeDomain: what staff typed reduced to the site the platform keys
//   on (bare host, plus the path on a path-addressed builder host). Strict:
//   "" when it cannot be parsed or the host has no dot.
// - siteKey: a light key for matching and lookups (lower case, no scheme,
//   no www., no trailing slash). It keeps any path as typed, so it is not a
//   stand-in for normalizeDomain.
//
// Ported from NewScanModal, which still keeps its own copy (without the
// dot rule and the path-addressed guard in droppedPath).

/**
 * A host where the PATH is the site, not a page on it: a free Wix site has no
 * other address. Deliberately a list of one, matching the backend's
 * PATH_SCOPED_BUILDER_HOSTS.
 */
export function isPathAddressedHost(host: string): boolean {
    return /(^|\.)wixsite\.com$/.test(host);
}

/**
 * Reduce whatever staff typed or pasted (a bare domain, a full URL, a www.
 * host) to the site the platform keys on: the bare host, plus the path on a
 * path-addressed builder host. "" when it cannot be parsed.
 */
export function normalizeDomain(raw: string): string {
    const value = raw.trim().toLowerCase();
    if (!value) return "";
    try {
        const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(withProtocol);
        const host = url.hostname.replace(/^www\./, "");
        if (!host.includes(".")) return "";
        if (isPathAddressedHost(host)) {
            const path = url.pathname.replace(/\/+$/, "");
            if (path) return `${host}${path}`;
        }
        return host;
    } catch {
        return "";
    }
}

/** The host half of a normalised domain, which may carry a path. */
export function hostOf(normalized: string): string {
    return normalized.split("/")[0];
}

/**
 * The path a pasted address carried that the normaliser dropped ("/clinic/kl"
 * from "site.com/clinic/kl"), or null when it carried none or was kept.
 */
export function droppedPath(raw: string): string | null {
    const value = raw.trim();
    if (!value) return null;
    try {
        const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(withProtocol);
        const path = url.pathname.replace(/\/+$/, "");
        if (!path || isPathAddressedHost(url.hostname.replace(/^www\./, "").toLowerCase())) return null;
        return path;
    } catch {
        return null;
    }
}

/** A light matching key: lower case, no scheme, no www., no trailing slash. Paths are kept. */
export function siteKey(domain: string): string {
    return domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/+$/, "");
}
